import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EditIcon from '@mui/icons-material/Edit';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import swimService from '../../api/services/swimService';
import memberService from '../../api/services/memberService';
import { useSnackbar } from '../common/SnackbarProvider';
import { MeetEvent, RosterAttendee, SwimCompetitionRow, TeamSwimmer } from '../../types/swim';
import SwimmerContactsDialog from './SwimmerContactsButton';

interface Props {
  competition: SwimCompetitionRow;
  onClose: () => void;
}

// Mirrors api/models/SwimMeetEvent_model.py — up to 3 individual events; relays don't count.
const MAX_INDIVIDUAL_EVENTS = 3;

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}
function fmtTime(value: string | null): string {
  if (!value) return '';
  const d = value.includes('T') ? new Date(value) : new Date(`1970-01-01T${value}`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

// Age brackets + open group (matches SwimMeetEvent_model). A swimmer sees their
// gender's events in their league-age group plus the open 18 & Under events.
const OPEN_GROUP = '18 & Under';
function ageGroupForAge(age: number | null): string | null {
  if (age == null) return null;
  if (age >= 19) return 'Adult';
  if (age <= 6) return '6 & Under';
  if (age <= 8) return '7-8';
  if (age <= 10) return '9-10';
  if (age <= 12) return '11-12';
  if (age <= 14) return '13-14';
  if (age <= 16) return '15-16';
  return '17-18';
}

// The seven age-group brackets, for the coach's gender/age filter.
const AGE_GROUPS = ['6 & Under', '7-8', '9-10', '11-12', '13-14', '15-16', '17-18', 'Adult'];

/** Coach view of a meet: team-wide roster. Each swimmer is read-only until the
 *  coach taps Edit (guards against accidental changes); editing unlocks the
 *  event picker + remove. A swimmer not RSVP'd can be added at the bottom. */
const MeetDetailDialog: React.FC<Props> = ({ competition, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const snackbar = useSnackbar();
  const [swimmers, setSwimmers] = useState<RosterAttendee[]>([]);
  const [teamSwimmers, setTeamSwimmers] = useState<TeamSwimmer[]>([]);
  const [meetEvents, setMeetEvents] = useState<MeetEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingMember, setSavingMember] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  // Which roster card's contacts popup is open. Whole card is the tap target;
  // Edit + the event picker stopPropagation so they don't open it.
  const [openContactId, setOpenContactId] = useState<string | null>(null);
  const [addValue, setAddValue] = useState<TeamSwimmer | null>(null);
  const [filterGender, setFilterGender] = useState<'All' | 'Female' | 'Male'>('All');
  const [filterGroup, setFilterGroup] = useState<string>('All');

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const [roster, events, team] = await Promise.all([
          swimService.getCompetitionRoster(competition.id),
          swimService.listMeetEvents(),
          swimService.getTeamSwimmers(competition.id),
        ]);
        setSwimmers(roster.swimmers ?? []);
        setMeetEvents(events);
        setTeamSwimmers(team ?? []);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load meet');
      } finally {
        setLoading(false);
      }
    })();
  }, [competition.id]);

  const catalogMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const e of meetEvents) m[e.code] = e.label;
    return m;
  }, [meetEvents]);

  const attendingIds = useMemo(() => new Set(swimmers.map((s) => s.member_id)), [swimmers]);
  const addable = useMemo(
    () => teamSwimmers.filter((t) => !attendingIds.has(t.member_id)),
    [teamSwimmers, attendingIds],
  );

  const filteredSwimmers = useMemo(() => {
    const matchesGender = (g: string | null | undefined) => {
      if (filterGender === 'All') return true;
      if (filterGender === 'Female') return g === 'Girls' || g === 'Women';
      return g === 'Boys' || g === 'Men';
    };
    return swimmers.filter(
      (s) => matchesGender(s.gender) && (filterGroup === 'All' || ageGroupForAge(s.age) === filterGroup),
    );
  }, [swimmers, filterGender, filterGroup]);

  function setSaving(memberId: string, on: boolean) {
    setSavingMember((prev) => {
      const next = new Set(prev);
      if (on) next.add(memberId);
      else next.delete(memberId);
      return next;
    });
  }

  async function applyAttendance(
    s: { member_id: string; first_name: string; last_name: string; age: number | null; gender?: string | null },
    attending: boolean,
    eventCodes: string[],
  ) {
    setSaving(s.member_id, true);
    try {
      const result = await swimService.setCompetitionAttendance(
        competition.id,
        { member_id: s.member_id },
        attending,
        eventCodes,
      );
      setSwimmers((prev) => {
        if (!attending) return prev.filter((x) => x.member_id !== s.member_id);
        if (prev.some((x) => x.member_id === s.member_id)) {
          return prev.map((x) =>
            x.member_id === s.member_id ? { ...x, event_codes: result.event_codes } : x,
          );
        }
        const next: RosterAttendee[] = [
          ...prev,
          {
            member_id: s.member_id,
            first_name: s.first_name,
            last_name: s.last_name,
            age: s.age,
            gender: s.gender ?? null,
            event_codes: result.event_codes,
          },
        ];
        next.sort(
          (a, b) =>
            (a.last_name || '').localeCompare(b.last_name || '') ||
            (a.first_name || '').localeCompare(b.first_name || ''),
        );
        return next;
      });
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Failed to update attendance');
    } finally {
      setSaving(s.member_id, false);
    }
  }

  function toggleEvent(s: RosterAttendee, code: string) {
    const cur = s.event_codes ?? [];
    const next = cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code];
    void applyAttendance(s, true, next);
  }

  async function setGender(s: RosterAttendee, gender: 'female' | 'male') {
    setSaving(s.member_id, true);
    try {
      await memberService.setMemberGender(s.member_id, gender);
      const label = gender === 'female' ? 'Girls' : 'Boys';
      setSwimmers((prev) => prev.map((x) => (x.member_id === s.member_id ? { ...x, gender: label } : x)));
      setTeamSwimmers((prev) => prev.map((x) => (x.member_id === s.member_id ? { ...x, gender: label } : x)));
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Failed to set gender');
    } finally {
      setSaving(s.member_id, false);
    }
  }

  function renderEventPicker(s: RosterAttendee) {
    const codes = s.event_codes ?? [];
    const group = ageGroupForAge(s.age);
    const g = s.gender ?? null;
    // Events this swimmer may enter: their gender, in their league-age group
    // or the open 18 & Under group.
    const available = meetEvents.filter(
      (e) => (!g || e.gender === g) && (e.age_group === OPEN_GROUP || e.age_group === group),
    );
    // Keep any already-selected event visible even if outside the above.
    const availCodes = new Set(available.map((e) => e.code));
    const shown = [
      ...available,
      ...meetEvents.filter((e) => codes.includes(e.code) && !availCodes.has(e.code)),
    ].sort(
      (a, b) =>
        a.age_group_order - b.age_group_order ||
        (a.distance_yards ?? 0) - (b.distance_yards ?? 0) ||
        a.label.localeCompare(b.label),
    );
    const atLimit = codes.length >= MAX_INDIVIDUAL_EVENTS;
    const saving = savingMember.has(s.member_id);
    return (
      <Stack spacing={1} sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {[g, group].filter(Boolean).join(' · ')}
          {g || group ? ' — ' : ''}
          {codes.length}/{MAX_INDIVIDUAL_EVENTS} events (relays don't count)
        </Typography>
        {!g && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant="caption" color="warning.main">No gender set —</Typography>
            <Button size="small" variant="outlined" disabled={saving} onClick={() => void setGender(s, 'female')}>Female</Button>
            <Button size="small" variant="outlined" disabled={saving} onClick={() => void setGender(s, 'male')}>Male</Button>
          </Stack>
        )}
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {shown.map((evt) => {
            const selected = codes.includes(evt.code);
            const disabled = saving || (atLimit && !selected);
            return (
              <Chip
                key={evt.code}
                size="small"
                label={evt.label}
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                onClick={() => toggleEvent(s, evt.code)}
                disabled={disabled}
              />
            );
          })}
        </Stack>
      </Stack>
    );
  }

  function renderSwimmer(s: RosterAttendee) {
    const saving = savingMember.has(s.member_id);
    const isEditing = editingId === s.member_id;
    const codes = s.event_codes ?? [];
    return (
      <Box
        key={s.member_id}
        onClick={() => setOpenContactId(s.member_id)}
        onKeyDown={(ev) => {
          if ((ev.key === 'Enter' || ev.key === ' ') && ev.target === ev.currentTarget) {
            ev.preventDefault();
            setOpenContactId(s.member_id);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Contact info for ${s.first_name} ${s.last_name}`}
        sx={{ p: 1.5, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* The whole card opens the contacts popup (tap target > name alone);
                Edit + the event picker below stopPropagation so they don't. */}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {`${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() || '—'}
              {s.age != null && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                  age {s.age}
                </Typography>
              )}
            </Typography>
          </Box>
          {saving && <CircularProgress size={14} />}
          {isEditing ? (
            <Button size="small" onClick={(ev) => { ev.stopPropagation(); setEditingId(null); }}>Done</Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={(ev) => { ev.stopPropagation(); setEditingId(s.member_id); }}
            >
              Edit
            </Button>
          )}
        </Stack>

        {isEditing ? (
          <Box onClick={(ev) => ev.stopPropagation()}>
            {renderEventPicker(s)}
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
              <Button
                size="small"
                color="error"
                startIcon={<PersonRemoveIcon />}
                disabled={saving}
                onClick={() => {
                  void applyAttendance(s, false, []);
                  setEditingId(null);
                }}
              >
                Remove from meet
              </Button>
            </Stack>
          </Box>
        ) : codes.length === 0 ? (
          <Typography variant="caption" color="text.secondary">No events entered yet</Typography>
        ) : (
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
            {codes.map((c) => (
              <Chip key={c} size="small" label={catalogMap[c] ?? c} variant="outlined" />
            ))}
          </Stack>
        )}
        <SwimmerContactsDialog
          open={openContactId === s.member_id}
          onClose={() => setOpenContactId(null)}
          firstName={s.first_name}
          lastName={s.last_name}
          age={s.age}
          contact={{
            parent_name: s.parent_name,
            parent_phone: s.parent_phone,
            emergency_contact_name: s.emergency_contact_name,
            emergency_contact_phone: s.emergency_contact_phone,
            allergies: s.allergies,
            medical_conditions: s.medical_conditions,
          }}
        />
      </Box>
    );
  }

  function exportPdf() {
    const eventByCode = new Map(meetEvents.map((e) => [e.code, e]));
    const byEvent = new Map<string, RosterAttendee[]>();
    for (const s of filteredSwimmers) {
      for (const c of s.event_codes ?? []) {
        const list = byEvent.get(c) ?? [];
        list.push(s);
        byEvent.set(c, list);
      }
    }
    const codes = Array.from(byEvent.keys()).sort((a, b) => {
      const ea = eventByCode.get(a);
      const eb = eventByCode.get(b);
      return (
        (ea?.age_group_order ?? 99) - (eb?.age_group_order ?? 99) ||
        (ea?.distance_yards ?? 0) - (eb?.distance_yards ?? 0) ||
        (ea?.label ?? a).localeCompare(eb?.label ?? b)
      );
    });
    const esc = (x: string) =>
      x.replace(/[&<>]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch] as string));
    const filterNote = [
      filterGender !== 'All' ? filterGender : null,
      filterGroup !== 'All' ? filterGroup : null,
    ]
      .filter(Boolean)
      .join(' · ');
    const rowsHtml = codes
      .map((c) => {
        const e = eventByCode.get(c);
        const title = e ? `${e.gender} ${e.age_group} — ${e.label}` : c;
        const entered = (byEvent.get(c) ?? [])
          .slice()
          .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
        const names = entered
          .map((s) => `${esc(s.first_name)} ${esc(s.last_name)}${s.age != null ? ` (${s.age})` : ''}`)
          .join(', ');
        return `<tr><td class="evt">${esc(title)}</td><td class="cnt">${entered.length}</td><td>${names}</td></tr>`;
      })
      .join('');
    const html =
      `<!doctype html><html><head><meta charset="utf-8"><title>${esc(competition.name)} — Entries</title>` +
      `<style>body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;margin:24px;color:#111}` +
      `h1{font-size:20px;margin:0 0 2px}.sub{color:#555;font-size:13px;margin-bottom:16px}` +
      `table{border-collapse:collapse;width:100%;font-size:13px}` +
      `th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;vertical-align:top}` +
      `th{background:#f3f3f3}.evt{font-weight:600;white-space:nowrap}.cnt{text-align:center;width:36px}` +
      `@media print{body{margin:0}}</style></head><body>` +
      `<h1>${esc(competition.name)} — Meet Entries</h1>` +
      `<div class="sub">${esc(fmtDate(competition.competition_date))}` +
      `${competition.location ? ` · ${esc(competition.location)}` : ''}` +
      `${filterNote ? ` · Filter: ${esc(filterNote)}` : ''} · ${filteredSwimmers.length} swimmers</div>` +
      `<table><thead><tr><th>Event</th><th class="cnt">#</th><th>Swimmers</th></tr></thead>` +
      `<tbody>${rowsHtml || '<tr><td colspan="3">No entries.</td></tr>'}</tbody></table>` +
      `<script>window.onload=function(){window.print();}</script></body></html>`;
    const w = window.open('', '_blank');
    if (!w) {
      snackbar.error('Pop-up blocked — allow pop-ups to export the PDF.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  }

  return (
    <Dialog open fullScreen={fullScreen} fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <EmojiEventsIcon sx={{ color: theme.palette.warning.main }} />
          <span>{competition.name}</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={0.25} sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {fmtDate(competition.competition_date)}
            {competition.start_time ? ` · ${fmtTime(competition.start_time)}` : ''}
          </Typography>
          {competition.location && <Typography variant="body2" color="text.secondary">{competition.location}</Typography>}
          {competition.host_team && <Typography variant="body2" color="text.secondary">Host: {competition.host_team}</Typography>}
          {competition.league && (
            <Typography variant="body2" color="text.secondary">
              {competition.league.name}
              {competition.league.age_up_date ? ` · age groups as of ${fmtDate(competition.league.age_up_date)}` : ''}
            </Typography>
          )}
          {competition.notes && <Typography variant="body2" sx={{ mt: 0.5 }}>{competition.notes}</Typography>}
        </Stack>

        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        ) : (
          <>
            <Stack direction="row" alignItems="center" sx={{ mt: 2, mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 110 }}>
                Attending ({filteredSwimmers.length}
                {filteredSwimmers.length !== swimmers.length ? ` of ${swimmers.length}` : ''})
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={filterGender}
                onChange={(_e, v) => v && setFilterGender(v)}
              >
                <ToggleButton value="All">All</ToggleButton>
                <ToggleButton value="Female">Female</ToggleButton>
                <ToggleButton value="Male">Male</ToggleButton>
              </ToggleButtonGroup>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel id="meet-filter-group">Age group</InputLabel>
                <Select
                  labelId="meet-filter-group"
                  label="Age group"
                  value={filterGroup}
                  onChange={(e) => setFilterGroup(e.target.value)}
                >
                  <MenuItem value="All">All ages</MenuItem>
                  {AGE_GROUPS.map((gp) => (
                    <MenuItem key={gp} value={gp}>{gp}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            {swimmers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No swimmers yet — add one below.</Typography>
            ) : filteredSwimmers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No swimmers match this filter.</Typography>
            ) : (
              <Stack spacing={1}>{filteredSwimmers.map((s) => renderSwimmer(s))}</Stack>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Add a swimmer</Typography>
            {addable.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Every team swimmer is already attending.</Typography>
            ) : (
              <Autocomplete
                size="small"
                options={addable}
                value={addValue}
                onChange={(_, val) => {
                  if (val) {
                    void applyAttendance(val, true, []);
                    setEditingId(val.member_id);
                    setAddValue(null);
                  }
                }}
                getOptionLabel={(o) => `${o.first_name} ${o.last_name}${o.age != null ? ` · ${o.age}` : ''}`}
                isOptionEqualToValue={(o, v) => o.member_id === v.member_id}
                renderInput={(params) => <TextField {...params} placeholder="Search swimmers…" />}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<PictureAsPdfIcon />}
          onClick={exportPdf}
          disabled={loading || swimmers.length === 0}
          sx={{ mr: 'auto' }}
        >
          Export PDF
        </Button>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MeetDetailDialog;
